import { Box, Button, Card, CardContent, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { CheckCircleOutline as CheckIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';

import { dialogOpen, dialogClose } from '@/modules/MuiDialog';
import makeErrorString from '@/utils/makeErrorString';

import { useSubscribe } from '@/hooks/queries/subscription/useSubscribe';
import { useChangePlan } from '@/hooks/queries/subscription/useChangePlan';
import { useChangePlanPreview } from '@/hooks/queries/subscription/useChangePlanPreview';
import type {
    BillingCycle,
    ChangePlanPreviewDto,
    PaymentMethodDto,
    SubscriptionPlanDto,
} from '@/types/subscription';
import { formatLocaleDate, formatLocaleNumber } from '@/utils/formatter';

import { ACCENT, ACCENT_HOVER, ACCENT_TINT, ACCENT_TINT_STRONG } from '../_shared';
import { useNicePayBillingFlow } from './useNicePayBillingFlow';
import { openChangePlanDialog } from './ChangePlanDialog';

// SVG noise — 카드 표면에 미세한 텍스처
const NOISE_TEXTURE =
    "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E\")";

interface PlanCardProps {
    plan: SubscriptionPlanDto;
    cycle: BillingCycle;
    currentPlanCode?: string;
    currentBillingCycle?: BillingCycle;
    hasActiveSubscription: boolean;
    hasPaymentMethod: boolean;
    defaultPaymentMethod?: PaymentMethodDto;
}

const PlanCard = ({
    plan,
    cycle,
    currentPlanCode,
    currentBillingCycle,
    hasActiveSubscription,
    hasPaymentMethod,
    defaultPaymentMethod,
}: PlanCardProps) => {
    const isCurrent = plan.code === currentPlanCode && cycle === currentBillingCycle;
    const isFree = plan.monthlyPrice === 0;
    const subscribable = plan.isSubscribable === 't';
    const isRecommended = plan.tier === 2;

    const priceRow = plan.prices?.find((p) => p.billingCycle === cycle);
    const cycleAmount = isFree ? 0 : priceRow?.amount;
    const originalAmount = priceRow?.originalAmount ?? null;
    const periodLabel = cycle === 'yearly' ? '년' : '월';
    const supportsThisCycle = isFree || cycleAmount !== undefined;

    const subscribe = useSubscribe();
    const changePlan = useChangePlan();
    const preview = useChangePlanPreview();
    const billing = useNicePayBillingFlow();

    const handleSubscribeFlow = async () => {
        const paymentMethodId = isFree ? undefined : defaultPaymentMethod?.id;
        try {
            await subscribe.mutateAsync({ planCode: plan.code, billingCycle: cycle, paymentMethodId });
            toast.success(`${plan.name} 구독을 시작했어요`);
        } catch (e) {
            toast.error(makeErrorString(e, '구독을 시작할 수 없어요'));
        }
    };

    const handleChangeFlow = async (previewData: ChangePlanPreviewDto) => {
        try {
            await changePlan.mutateAsync({ planCode: plan.code, billingCycle: cycle });
            const msg =
                previewData.type === 'upgrade'
                    ? `${plan.name} 플랜으로 변경했어요`
                    : previewData.type === 'free_downgrade'
                    ? `${formatLocaleDate(previewData.effectiveAt)}에 자동 해지될 예정이에요`
                    : `${formatLocaleDate(previewData.effectiveAt)}에 ${plan.name}로 변경될 예정이에요`;
            toast.success(msg);
        } catch (e) {
            toast.error(makeErrorString(e, '플랜 변경에 실패했어요'));
        }
    };

    // 변경 확인 다이얼로그 — ChangePlanDialog 컴포넌트에서 가이드 박스 포함해 보여줌

    const handleClick = async () => {
        if (!subscribable) {
            toast.info('도입 문의는 영업팀으로 연락 주세요');
            return;
        }
        if (!isFree && !supportsThisCycle) {
            toast.info('이 플랜은 해당 결제 주기를 지원하지 않아요');
            return;
        }

        if (!isFree && !hasPaymentMethod) {
            dialogOpen({
                title: '결제 카드 등록 필요',
                message: '유료 플랜을 시작하려면 먼저 결제 카드를 등록해주세요.',
                confirmText: '카드 등록하기',
                onConfirmed: () => {
                    billing.start({
                        onComplete: async () => {
                            if (hasActiveSubscription) {
                                try {
                                    const p = await preview.mutateAsync({
                                        planCode: plan.code,
                                        billingCycle: cycle,
                                    });
                                    await handleChangeFlow(p);
                                } catch (e) {
                                    toast.error(makeErrorString(e, '플랜 변경 정보를 가져오지 못했어요'));
                                }
                            } else {
                                await handleSubscribeFlow();
                            }
                        },
                    });
                },
            });
            return;
        }

        if (!hasActiveSubscription) {
            const priceText = isFree
                ? '결제 없이 즉시 시작돼요.'
                : `지금 ₩${formatLocaleNumber(cycleAmount ?? 0)} 이 결제되고 ${
                      cycle === 'yearly' ? '1년' : '한 달'
                  } 동안 이용할 수 있어요.`;
            dialogOpen({
                title: `${plan.name} (${cycle === 'yearly' ? '연' : '월'} 결제)을(를) 구독할까요?`,
                message: priceText,
                confirmText: '구독하기',
                onConfirmed: async () => {
                    await handleSubscribeFlow();
                    dialogClose();
                },
            });
            return;
        }

        let p: ChangePlanPreviewDto;
        try {
            p = await preview.mutateAsync({ planCode: plan.code, billingCycle: cycle });
        } catch (e) {
            toast.error(makeErrorString(e, '플랜 변경 정보를 가져오지 못했어요'));
            return;
        }

        if (p.type === 'same') {
            toast.info('이미 사용 중인 플랜이에요');
            return;
        }
        if (p.type === 'unavailable') {
            toast.error('지금은 변경할 수 없어요');
            return;
        }

        openChangePlanDialog({
            preview: p,
            planName: plan.name,
            onConfirm: () => handleChangeFlow(p),
        });
    };

    const buttonLabel = !subscribable
        ? '문의하기'
        : isCurrent
        ? '사용 중'
        : hasActiveSubscription
        ? '변경하기'
        : '구독하기';

    const busy = subscribe.isPending || changePlan.isPending || preview.isPending || billing.isPending;

    // ── 핫 글로우 톤
    const showGlow = isRecommended || isCurrent;
    const glowIntensity = isRecommended ? 0.5 : isCurrent ? 0.35 : 0;

    return (
        <Card
            variant="outlined"
            sx={{
                position: 'relative',
                overflow: 'hidden',
                bgcolor: '#FFFFFF',
                borderRadius: 3,
                border: isCurrent
                    ? `2px solid ${ACCENT}`
                    : isRecommended
                    ? '1px solid rgba(251,109,72,0.25)'
                    : '1px solid rgba(0,0,0,0.08)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition:
                    'transform 280ms cubic-bezier(.2,.8,.2,1), box-shadow 280ms ease, border-color 240ms ease',
                willChange: 'transform',

                // 미세한 노이즈 텍스처 (모든 카드)
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    pointerEvents: 'none',
                    backgroundImage: NOISE_TEXTURE,
                    opacity: 0.035,
                    mixBlendMode: 'multiply',
                    zIndex: 0,
                },

                // 추천/현재 카드 — 우측 상단 ACCENT 광원
                ...(showGlow && {
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: '-40%',
                        right: '-30%',
                        width: 320,
                        height: 320,
                        borderRadius: '50%',
                        background: `radial-gradient(circle, rgba(251,109,72,${glowIntensity}) 0%, rgba(251,109,72,0) 65%)`,
                        filter: 'blur(56px)',
                        pointerEvents: 'none',
                        zIndex: 0,
                        transition: 'filter 320ms ease, transform 320ms ease, opacity 320ms ease',
                    },
                }),

                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: isRecommended
                        ? '0 24px 64px rgba(251,109,72,0.22), 0 8px 20px rgba(251,109,72,0.10)'
                        : isCurrent
                        ? '0 24px 60px rgba(251,109,72,0.16)'
                        : '0 14px 40px rgba(0,0,0,0.07)',
                    ...(showGlow && {
                        '&::before': {
                            filter: 'blur(72px)',
                            transform: 'scale(1.12)',
                        },
                    }),
                },
            }}
        >
            {isCurrent && (
                <Chip
                    size="small"
                    label="이용 중"
                    sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        zIndex: 2,
                        bgcolor: ACCENT,
                        color: '#fff',
                        fontWeight: 700,
                        boxShadow: '0 4px 16px rgba(251,109,72,0.45)',
                    }}
                />
            )}
            <CardContent
                sx={{
                    py: 3,
                    px: 2.75,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    flexGrow: 1,
                    position: 'relative',
                    zIndex: 1, // 글로우/노이즈 위로
                }}
            >
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                        {plan.name}
                    </Typography>
                    {isRecommended && !isCurrent && (
                        <Chip
                            size="small"
                            label="추천"
                            sx={{
                                bgcolor: ACCENT_TINT_STRONG,
                                color: ACCENT,
                                fontWeight: 700,
                                boxShadow: '0 0 12px rgba(251,109,72,0.25)',
                            }}
                        />
                    )}
                </Stack>

                <Stack direction="row" alignItems="baseline" spacing={0.5}>
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 900,
                            color: ACCENT,
                            letterSpacing: '-0.02em',
                            textShadow: showGlow
                                ? `0 0 28px rgba(251,109,72,0.45), 0 0 8px rgba(251,109,72,0.25)`
                                : 'none',
                            transition: 'text-shadow 240ms ease',
                        }}
                    >
                        {isFree
                            ? '무료'
                            : !supportsThisCycle
                            ? '문의'
                            : `₩${formatLocaleNumber(cycleAmount ?? 0)}`}
                    </Typography>
                    {!isFree && supportsThisCycle && (
                        <Typography variant="body2" color="text.secondary">
                            / {periodLabel}
                        </Typography>
                    )}
                </Stack>

                {!isFree && originalAmount && originalAmount > (cycleAmount ?? 0) && (
                    <Stack direction="row" alignItems="baseline" spacing={0.75} sx={{ mt: -0.5 }}>
                        <Typography
                            variant="caption"
                            sx={{ color: 'text.disabled', textDecoration: 'line-through' }}
                        >
                            ₩{formatLocaleNumber(originalAmount)}
                        </Typography>
                        <Chip
                            size="small"
                            label={`${Math.round((1 - (cycleAmount ?? 0) / originalAmount) * 100)}% 할인`}
                            sx={{
                                height: 18,
                                fontSize: 10,
                                fontWeight: 700,
                                bgcolor: ACCENT_TINT_STRONG,
                                color: ACCENT,
                                boxShadow: '0 0 10px rgba(251,109,72,0.25)',
                            }}
                        />
                    </Stack>
                )}

                {plan.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {plan.description}
                    </Typography>
                )}

                <Stack spacing={0.75} sx={{ mt: 0.5, flexGrow: 1 }}>
                    {plan.planFeatures?.map((pf) => (
                        <Stack key={pf.feature.code} direction="row" spacing={1} alignItems="center">
                            <CheckIcon sx={{ color: ACCENT, fontSize: 18 }} />
                            <Typography variant="body2" color="text.primary">
                                {pf.displayText ?? `${pf.feature.name}: ${pf.value}${pf.feature.unit ?? ''}`}
                            </Typography>
                        </Stack>
                    ))}
                </Stack>

                <Box sx={{ pt: 1 }}>
                    <Button
                        fullWidth
                        onClick={handleClick}
                        disabled={isCurrent || busy}
                        variant={isCurrent ? 'outlined' : 'contained'}
                        disableElevation
                        startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}
                        sx={{
                            position: 'relative',
                            bgcolor: isCurrent ? 'transparent' : ACCENT,
                            color: isCurrent ? ACCENT : '#fff',
                            borderColor: ACCENT,
                            fontWeight: 700,
                            textTransform: 'none',
                            borderRadius: 2,
                            py: 1.2,
                            cursor: isCurrent ? 'default' : 'pointer',
                            boxShadow: isCurrent
                                ? 'none'
                                : isRecommended
                                ? '0 8px 24px rgba(251,109,72,0.4), 0 0 0 1px rgba(251,109,72,0.2) inset'
                                : '0 4px 16px rgba(251,109,72,0.22)',
                            transition: 'transform 160ms ease, background-color 160ms ease, box-shadow 200ms ease',
                            willChange: 'transform',
                            '&:hover': {
                                bgcolor: isCurrent ? ACCENT_TINT : ACCENT_HOVER,
                                transform: isCurrent ? 'none' : 'translateY(-1px)',
                                boxShadow: isCurrent
                                    ? 'none'
                                    : '0 12px 32px rgba(251,109,72,0.5)',
                            },
                            '&:active': { transform: 'translateY(0)' },
                            '&.Mui-disabled': {
                                bgcolor: isCurrent ? 'transparent' : ACCENT,
                                opacity: isCurrent ? 1 : 0.6,
                                color: isCurrent ? ACCENT : '#fff',
                                borderColor: ACCENT,
                            },
                        }}
                    >
                        {busy ? '처리 중…' : buttonLabel}
                    </Button>
                </Box>
            </CardContent>
        </Card>
    );
};

export default PlanCard;
